/** @graphql agg */

import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

import { getAGGClient } from '@confluence/graphql';

import type { useDoesTenantHavePaymentDetailsOnFileQuery } from './__types__/useDoesTenantHavePaymentDetailsOnFileQuery';

interface UseDoesTenantHavePaymentDetailsOnFileProps {
	cloudId: string;
	skip?: boolean;
}

type useDoesTenantHavePaymentDetailsOnFileReturn = {
	hasPaymentDetailsOnFile: boolean;
	loading: boolean;
	error: Error | undefined;
};

/**
 * Custom hook that determines if a tenant is currently in a trial period
 * without having payment details on file.
 *
 * @param cloudId - The tenant's cloud identifier
 * @param skip - Whether to skip the query execution (defaults to false)
 * @returns Object containing payment details status, loading state, and any errors
 */
export const useDoesTenantHavePaymentDetailsOnFile = ({
	cloudId,
	skip = false,
}: UseDoesTenantHavePaymentDetailsOnFileProps): useDoesTenantHavePaymentDetailsOnFileReturn => {
	const aggClient = getAGGClient();
	const { data, error, loading } = useQuery<useDoesTenantHavePaymentDetailsOnFileQuery>(
		gql`
			query useDoesTenantHavePaymentDetailsOnFileQuery($cloudId: ID!) {
				tenantContexts(cloudIds: [$cloudId]) {
					entitlementInfo(hamsProductKey: "confluence.ondemand") {
						entitlement {
							transactionAccount {
								isBillToPresent
							}
						}
					}
				}
			}
		`,
		{
			fetchPolicy: 'cache-and-network',
			skip: !cloudId || skip,
			variables: { cloudId },
			client: aggClient,
		},
	);

	const transactionAccount =
		data?.tenantContexts?.[0]?.entitlementInfo?.entitlement?.transactionAccount;

	const hasPaymentDetailsOnFile = !!transactionAccount?.isBillToPresent;

	return {
		hasPaymentDetailsOnFile,
		loading,
		error,
	};
};
